import { cn } from 'lib/utils';
import Text from '../Text';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from 'components/ui/select';
import { ReactSVG } from 'react-svg';
import {
  cross_icon,
  dropdown_arrow_icon,
  search_icon
} from 'asset/icons';
import { useState } from 'react';
import { searchArray } from 'utils/functions';
import InputField from '../InputField';

interface DropdownOption {
  label: string;
  id: string;
}

interface DropdownProps {
  label?: string;
  value: any;
  options: DropdownOption[];
  placeholder?: string;
  onChange: (value: any) => void;
  className?: string;
  valueKey?: string;
  isSearchable?: boolean;
}

const Dropdown = ({
  label,
  value,
  options,
  placeholder = 'Select...',
  valueKey,
  onChange,
  className,
  isSearchable
}: DropdownProps) => {
  const [search, setSearch] = useState('');

  const filteredOptions = isSearchable
    ? searchArray(options, search)
    : options;

  const isArrayValue = Array.isArray(value);

  const handleChange = (id: string) => {
    const selected = options.find(
      (opt) => opt?.id?.toString() === id?.toString()
    );

    if (selected) onChange(selected);
  };

  const getLabel = (item: any) =>
    valueKey ? item?.[valueKey] : item?.label || item;

  const displayValue = isArrayValue
    ? value?.map(getLabel)?.filter(Boolean)?.join(', ')
    : getLabel(value);

  const isSelected = (opt: DropdownOption) => {
    if (isArrayValue) {
      return value?.some(
        (item: any) => item?.id?.toString() === opt?.id?.toString()
      );
    }
    return value?.id?.toString() === opt?.id?.toString();
  };

 
  return (
    <div className={cn('flex flex-col gap-2 w-full', className)}>
      {label && <Text text={label} className='text-muted-foreground' />}

      {/* 🔥 WRAPPER FIX */}
      <div className="relative w-full">
        <Select value="" onValueChange={handleChange}>
          <SelectTrigger className="w-full bg-input rounded-full py-3 shadow-none pr-10">
            <SelectValue placeholder={displayValue || placeholder} />
          </SelectTrigger>

          <SelectContent
            className="max-h-56 rounded-lg shadow-lg border border-border"
            position="popper"
          >
            {/* SEARCH */}
            {isSearchable && (
              <div
                onKeyDown={(e) => e.stopPropagation()}
                className="mb-2"
              >
                <InputField
                  value={search}
                  onChange={(val) => setSearch(val)}
                  placeholder="Search..."
                  rightIcon={search_icon}
                />
              </div>
            )}

            {/* OPTIONS */}
            <div className="max-h-40 overflow-y-auto">
              {filteredOptions?.length ? (
                filteredOptions.map((opt) => (
                  <SelectItem
                    key={opt?.id}
                    value={opt?.id}
                    className={cn(
                      'rounded-md px-2 py-2 cursor-pointer hover:bg-muted',
                      isSelected(opt) && 'bg-primary/10'
                    )}
                  >
                    <span>{opt?.label}</span>
                  </SelectItem>
                ))
              ) : (
                <div className="text-sm text-muted-foreground px-2 py-2">
                  No results found
                </div>
              )}
            </div>
          </SelectContent>
        </Select>

        {/* ✅ CROSS BUTTON (OUTSIDE TRIGGER - FIXED) */}
        {displayValue && (
          <button
            type="button"

            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              onChange(null);
            }}
            className="absolute right-3 top-1/2 -translate-y-1/2 flex items-center justify-center z-10"
          >
            <ReactSVG src={cross_icon}
              beforeInjection={(svg) => {
                svg.setAttribute('style', 'width: 18px');
              }}
            />
          </button>
        )}

        {/* 🔽 DROPDOWN ICON */}
        {!displayValue && (
          <div className="absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none">
            <ReactSVG src={dropdown_arrow_icon} height={12} width={12} />
          </div>
        )}
      </div>
    </div>
  );
};

export default Dropdown;
